/**
 * AI Service
 * 
 * This service handles all AI-related operations for the Manchester Triage System,
 * including symptom extraction, triage enhancement, and complex case handling.
 * 
 * It provides a clean interface for making API calls to language models
 * while handling errors, fallbacks, and monitoring.
 */

import { TriageData, TriageOutcome, AIInsight, ExplanationFactor, UrgencyLevel } from '@/types/triage';

// Types for OpenAI API calls
interface GPTMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface GPTRequest {
  model?: string;
  messages: GPTMessage[];
  temperature?: number;
  max_tokens?: number;
  response_format?: { type: string };
}

interface GPTResponse {
  choices: {
    message: {
      content: string;
    };
  }[];
}

// Configuration for the AI service
interface AIServiceConfig {
  apiKey?: string;
  apiEndpoint?: string;
  baseModel: string;     // Base model for simple tasks
  advancedModel: string; // Advanced model for complex tasks
  maxRetries: number;
  timeoutMs: number;
  testingMode?: boolean; // Flag to force simulation mode
}

// Default configuration
const DEFAULT_CONFIG: AIServiceConfig = {
  baseModel: 'gpt-3.5-turbo',    // Cheaper model for basic tasks
  advancedModel: 'gpt-4-turbo',  // More capable model for complex cases
  maxRetries: 2,
  timeoutMs: 15000, // 15 seconds, clinical analysis might need a bit more time
  testingMode: false // Default to normal operation
};

// Service class for AI operations
export class AIService {
  private config: AIServiceConfig;
  
  constructor(config: Partial<AIServiceConfig> = {}) {
    // Apply default config with overrides
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      // Use environment variables as fallback for sensitive data
      apiKey: config.apiKey || import.meta.env.VITE_OPENAI_API_KEY,
      apiEndpoint: config.apiEndpoint || 'https://api.openai.com/v1/chat/completions',
    };
    
    // Log initialization status (without exposing API key)
    if (this.config.apiKey && !this.config.testingMode) {
      console.log('[AIService] Initialized with API key and models:', 
        `Base: ${this.config.baseModel}, Advanced: ${this.config.advancedModel}`);
    } else if (this.config.testingMode) {
      console.warn('[AIService] Running in testing simulation mode.');
    } else {
      console.warn('[AIService] No API key found. Running in fallback simulation mode.');
    }
  }
  
  /**
   * Make a call to the OpenAI GPT API
   * Handles authentication, error handling, and retries
   * 
   * @param request The GPT API request parameters
   * @returns The API response or null if failed
   */
  private async callGPT(request: GPTRequest): Promise<GPTResponse | null> {
    // Validate request parameters
    if (!request || !request.messages || !Array.isArray(request.messages) || request.messages.length === 0) {
      console.error('[AIService] Invalid request parameters:', request);
      return null;
    }

    // In testing mode, always return null to force fallback
    if (this.config.testingMode) {
      console.log('[AIService] Running in testing mode - simulating API failure');
      return null;
    }
    
    if (!this.config.apiKey) {
      console.warn('[AIService] No API key available. Cannot make API call.');
      return null;
    }

    // Ensure API endpoint is available
    if (!this.config.apiEndpoint) {
      console.error('[AIService] No API endpoint configured');
      return null;
    }
    
    const requestOptions = {
      model: request.model || this.config.baseModel, // Default to base model if not specified
      messages: request.messages,
      temperature: typeof request.temperature === 'number' ? request.temperature : 0.3,
      max_tokens: typeof request.max_tokens === 'number' ? request.max_tokens : 1000,
      response_format: request.response_format
    };
    
    let retries = 0;
    const maxRetries = typeof this.config.maxRetries === 'number' ? this.config.maxRetries : 2;
    const timeoutMs = typeof this.config.timeoutMs === 'number' ? this.config.timeoutMs : 10000;
    
    while (retries <= maxRetries) {
      try {
        console.log(`[AIService] Making API call (attempt ${retries + 1}/${maxRetries + 1})`);
        
        const response = await fetch(this.config.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.config.apiKey}`
          },
          body: JSON.stringify(requestOptions),
          signal: AbortSignal.timeout(timeoutMs)
        });
        
        if (!response.ok) {
          let errorMessage = `API error: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage += ` - ${JSON.stringify(errorData)}`;
          } catch (jsonError) {
            errorMessage += ` - Could not parse error response: ${await response.text()}`;
          }
          throw new Error(errorMessage);
        }
        
        const responseData = await response.json();
        console.log('[AIService] API call successful');
        return responseData;
      } catch (error) {
        console.error(`[AIService] API call failed (attempt ${retries + 1}/${maxRetries + 1}):`, error);
        retries++;
        
        if (retries > maxRetries) {
          console.error('[AIService] All retries failed. Giving up.');
          return null;
        }
        
        // Exponential backoff with jitter to prevent thundering herd
        const backoffTime = 1000 * Math.pow(2, retries) * (0.8 + Math.random() * 0.4);
        console.log(`[AIService] Retrying in ${Math.round(backoffTime / 1000)} seconds...`);
        await new Promise(resolve => setTimeout(resolve, backoffTime));
      }
    }
    
    return null;
  }
  
  /**
   * Extract symptoms from free text using NLP
   * Uses the base model as this task doesn't require advanced clinical reasoning
   * 
   * @param text Free text description of patient's condition
   * @returns Array of extracted symptoms
   */
  async extractSymptoms(text: string): Promise<string[]> {
    // Validate input
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      console.log('[AIService] No text provided for symptom extraction, returning empty array');
      return [];
    }

    // Truncate long text for logging
    const safeText = typeof text === 'string' ? text : String(text);
    const truncatedText = safeText.substring(0, 50) + (safeText.length > 50 ? '...' : '');
    console.log('[AIService] Extracting symptoms from text:', truncatedText);
    
    // Check if we should use fallback mode
    if (this.config.testingMode || !this.config.apiKey) {
      console.log('[AIService] Using fallback symptom extraction due to testing mode or missing API key');
      return this.extractSymptomsFallback(safeText);
    }
    
    try {
      // Call GPT with symptom extraction prompt
      const response = await this.callGPT({
        model: this.config.baseModel, // Use base model for symptom extraction
        messages: [
          {
            role: 'system',
            content: `You are a clinical AI assistant specializing in symptom extraction for the Manchester Triage System. 
            Extract all medical symptoms from the given clinical description and return ONLY a JSON object with a single 
            key named "symptoms" containing an array of symptom strings. Be thorough, clinically precise, and include 
            all symptoms mentioned explicitly or implicitly. Focus on clinical relevance for emergency triage.
            
            Example output format:
            {
              "symptoms": ["chest pain", "shortness of breath", "nausea"]
            }`
          },
          { role: 'user', content: safeText }
        ],
        temperature: 0.1, // Low temperature for consistent, focused responses
        response_format: { type: 'json_object' }
      });
      
      // Process API response
      if (response && response.choices && response.choices[0]?.message?.content) {
        try {
          const jsonResponse = JSON.parse(response.choices[0].message.content);
          if (jsonResponse.symptoms && Array.isArray(jsonResponse.symptoms)) {
            // Validate each symptom is a string
            const validSymptoms = jsonResponse.symptoms
              .filter(symptom => typeof symptom === 'string' && symptom.trim().length > 0)
              .map(symptom => symptom.trim());
              
            console.log(`[AIService] Successfully extracted ${validSymptoms.length} symptoms`);
            return validSymptoms;
          } else {
            console.warn('[AIService] API returned invalid symptoms format, using fallback');
          }
        } catch (parseError) {
          console.error('[AIService] Error parsing symptom extraction response:', parseError);
        }
      } else {
        console.warn('[AIService] API call did not return valid content, using fallback');
      }
      
      // If we couldn't parse a proper response, fall back
      return this.extractSymptomsFallback(safeText);
      
    } catch (error) {
      console.error('[AIService] Error extracting symptoms:', error);
      return this.extractSymptomsFallback(safeText);
    }
  }
  
  /**
   * Fallback symptom extraction when API is unavailable
   * Uses basic keyword matching for essential triage symptoms
   * 
   * @param text The clinical description text
   * @returns Array of detected symptoms
   */
  private extractSymptomsFallback(text: string): string[] {
    console.log('[AIService] Using fallback symptom extraction');
    
    // Validate input
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      console.warn('[AIService] Empty or invalid text provided to fallback symptom extraction');
      return [];
    }
    
    // Normalize text for consistent matching
    const normalizedText = text.toLowerCase().trim();
    
    // Common emergency triage symptoms organized by body system
    const keywords = [
      // Cardiorespiratory
      'pain', 'chest pain', 'shortness of breath', 'difficulty breathing', 'palpitations',
      'cough', 'wheezing', 'hemoptysis', 'tachypnea', 'dyspnea',
      
      // Neurological
      'headache', 'dizziness', 'confusion', 'altered consciousness', 'seizure',
      'weakness', 'numbness', 'tingling', 'paralysis', 'syncope', 'faint',
      
      // Gastrointestinal
      'nausea', 'vomiting', 'diarrhea', 'constipation', 'abdominal pain',
      'melena', 'hematemesis', 'jaundice', 'dysphagia',
      
      // General
      'fever', 'chills', 'fatigue', 'sweating', 'weight loss',
      
      // Musculoskeletal
      'back pain', 'joint pain', 'muscle pain', 'swelling', 'trauma', 'injury',
      
      // ENT/Respiratory
      'sore throat', 'ear pain', 'nasal congestion', 'rhinorrhea',
      
      // Dermatological
      'rash', 'itching', 'hives', 'burn', 'laceration', 'bleeding'
    ];
    
    try {
      // Find all matching symptoms
      const matchedSymptoms = keywords.filter(keyword => 
        normalizedText.includes(keyword.toLowerCase())
      );
      
      // Sort by length (descending) to prioritize more specific symptoms
      // e.g., prefer "chest pain" over just "pain"
      const sortedSymptoms = [...matchedSymptoms].sort((a, b) => b.length - a.length);
      
      // Remove subsumed symptoms (e.g., if "chest pain" is found, remove "pain")
      const filteredSymptoms = sortedSymptoms.filter((symptom, index) => {
        // Check if this symptom is a substring of any longer symptom that comes before it
        return !sortedSymptoms.slice(0, index).some(longerSymptom => 
          longerSymptom !== symptom && longerSymptom.includes(symptom)
        );
      });
      
      console.log(`[AIService] Fallback extraction found ${filteredSymptoms.length} symptoms`);
      return filteredSymptoms;
    } catch (error) {
      console.error('[AIService] Error in fallback symptom extraction:', error);
      return [];
    }
  }
  
  /**
   * Generate AI insights from triage data
   * Integrates with OpenAI GPT for enhanced clinical analysis
   * Uses tiered approach: base model for standard cases, advanced model for complex cases
   * 
   * @param triageData The patient's triage data
   * @param existingOutcome Optional existing triage outcome to enhance
   * @returns AI-generated insights
   */
  async generateAIInsights(triageData: TriageData, existingOutcome?: TriageOutcome): Promise<AIInsight> {
    // Validate input data
    if (!triageData) {
      console.error('[AIService] Invalid triage data provided to generateAIInsights');
      // Create a safe empty triage data object for the fallback
      const emptyData: TriageData = {
        patientId: '',
        symptoms: [],
        severity: 'unknown',
        duration: 'unknown',
        onset: 'unknown',
        additionalInfo: '',
        description: '',
        vitals: {},
        medicalHistory: [],
        allergies: [],
        medications: [],
        age: undefined,
        gender: undefined
      };
      return this.generateSimulatedAIInsights(emptyData);
    }
    
    // Create a safe copy of triage data with defaults
    const safeTriageData: TriageData = {
      patientId: triageData.patientId || '',
      symptoms: Array.isArray(triageData.symptoms) ? triageData.symptoms : [],
      severity: triageData.severity || 'unknown',
      duration: triageData.duration || 'unknown',
      onset: triageData.onset || 'unknown',
      additionalInfo: triageData.additionalInfo || '',
      description: typeof triageData.description === 'string' ? triageData.description : '',
      vitals: triageData.vitals || {},
      medicalHistory: Array.isArray(triageData.medicalHistory) ? triageData.medicalHistory : [],
      allergies: Array.isArray(triageData.allergies) ? triageData.allergies : [],
      medications: Array.isArray(triageData.medications) ? triageData.medications : [],
      age: typeof triageData.age === 'number' ? triageData.age : undefined,
      gender: triageData.gender
    };
    
    // Log triage data information for debugging
    console.log('[AIService] Generating AI insights for triage data', {
      symptomsCount: safeTriageData.symptoms.length,
      hasVitals: Object.keys(safeTriageData.vitals || {}).length > 0,
      hasMedicalHistory: (safeTriageData.medicalHistory || []).length > 0,
    });
    
    // If testing mode, no API key, or this is a simple case, use the simulated insights
    if (this.config.testingMode || !this.config.apiKey || safeTriageData.symptoms.length <= 1) {
      console.log('[AIService] Using simulated AI insights (testing mode or no API key or simple case)');
      return this.generateSimulatedAIInsights(safeTriageData);
    }
    
    try {
      // First check if this is a complex case requiring special handling
      const isComplex = await this.isComplexCase(safeTriageData);
      
      if (isComplex) {
        console.log('[AIService] Detected complex case, using specialized handling');
        return await this.handleComplexCase(safeTriageData, existingOutcome);
      }
      
      // For standard cases, enhance the triage with AI analysis
      return await this.enhanceTriage(safeTriageData, existingOutcome);
    } catch (error) {
      console.error('[AIService] Error generating AI insights:', error);
      // Fallback to simulation if API fails
      return this.generateSimulatedAIInsights(safeTriageData);
    }
  }
  
  /**
   * Determine if a case is complex and requires advanced analysis
   * Uses the base model as this is a preliminary assessment
   * 
   * @param triageData The patient's triage data
   * @returns True if the case is complex
   */
  private async isComplexCase(triageData: TriageData): Promise<boolean> {
    try {
      // Simple heuristic checks first to avoid unnecessary API calls
      const symptoms = triageData.symptoms.map(s => s.toLowerCase());
      
      // Check for unusual combinations that might indicate complexity
      const hasUnusualCombination = (
        (symptoms.includes('chest pain') && symptoms.includes('rash')) ||
        (symptoms.includes('headache') && symptoms.includes('vision changes') && symptoms.includes('neck stiffness')) ||
        (symptoms.some(s => s.includes('psychiatric')) && symptoms.some(s => s.includes('physical')))
      );
      
      // If we have obvious complexity, return true without API call
      if (hasUnusualCombination) return true;
      
      if (triageData.symptoms.length <= 2) {
        return false;
      }
      
      // Check for obvious complexity indicators in description
      const complexityIndicators = [
        'unusual', 'complex', 'atypical', 'rare', 'puzzling', 
        'contradictory', 'inconsistent', 'multiple systems'
      ];
      
      if (triageData.description && 
          complexityIndicators.some(indicator => 
            triageData.description.toLowerCase().includes(indicator))) {
        return true;
      }
      
      // For less obvious cases, use the base model to assess complexity
      if (!this.config.testingMode && this.config.apiKey && triageData.symptoms.length >= 3) {
        const response = await this.callGPT({
          model: this.config.baseModel, // Use base model for complexity assessment
          messages: [
            {
              role: 'system',
              content: `You are a clinical AI assistant specializing in emergency triage. 
              Analyze the patient data and determine if this is a complex case requiring special consideration.
              A complex case typically has unusual symptom combinations, atypical presentations, or multiple
              potentially conflicting conditions. Return ONLY a JSON object with a single key "isComplex" 
              containing a boolean value.`
            },
            { 
              role: 'user', 
              content: `Patient data: ${JSON.stringify({
                symptoms: triageData.symptoms,
                vitals: triageData.vitals,
                age: triageData.age,
                gender: triageData.gender,
                medicalHistory: triageData.medicalHistory,
                additionalInfo: triageData.additionalInfo
              })}` 
            }
          ],
          temperature: 0.1,
          response_format: { type: 'json_object' }
        });
        
        if (response && response.choices && response.choices[0]?.message?.content) {
          try {
            const jsonResponse = JSON.parse(response.choices[0].message.content);
            if (typeof jsonResponse.isComplex === 'boolean') {
              return jsonResponse.isComplex;
            }
          } catch (error) {
            console.error('[AIService] Error parsing complexity assessment:', error);
          }
        }
      }
      
      // Default to false if we couldn't determine complexity
      return false;
      
    } catch (error) {
      console.error('[AIService] Error determining case complexity:', error);
      return false;
    }
  }
  
  /**
   * Handle complex cases that require more advanced analysis
   * Uses the advanced model for better clinical reasoning
   * 
   * @param triageData The patient's triage data
   * @param existingOutcome Optional existing triage outcome
   * @returns AI-generated insights
   */
  private async handleComplexCase(triageData: TriageData, existingOutcome?: TriageOutcome): Promise<AIInsight> {
    console.log('[AIService] Handling complex case with specialized analysis');
    
    // If no triage data provided, return simulated insights with empty data
    if (!triageData) {
      console.error('[AIService] No triage data provided for complex case analysis');
      const emptyData: TriageData = { 
        symptoms: [], 
        vitals: {}, 
        severity: '', 
        duration: '', 
        onset: '', 
        medicalHistory: [], 
        medications: [], 
        allergies: [], 
        additionalInfo: '', 
        description: '' 
      };
      return this.generateSimulatedAIInsights(emptyData);
    }
    
    // Create a safe copy of triage data with defaults to prevent null reference errors
    const safeTriageData: TriageData = {
      symptoms: triageData.symptoms || [],
      vitals: triageData.vitals || {},
      age: triageData.age || undefined, // Use undefined for optional number field
      gender: triageData.gender || '',
      severity: triageData.severity || '',
      duration: triageData.duration || '',
      onset: triageData.onset || '',
      medicalHistory: triageData.medicalHistory || [],
      medications: triageData.medications || [],
      allergies: triageData.allergies || [],
      additionalInfo: triageData.additionalInfo || '',
      description: triageData.description || ''
    };
    
    // If testing mode or no API key, fall back to simulation
    if (this.config.testingMode || !this.config.apiKey) {
      return this.generateSimulatedAIInsights(safeTriageData);
    }
    
    try {
      // Prepare context about the existing outcome if available
      let existingOutcomeContext = '';
      if (existingOutcome && typeof existingOutcome === 'object') {
        existingOutcomeContext = `
        Current triage assessment:
        - Urgency: ${existingOutcome.urgency || 'Not specified'}
        - Manchester Category: ${existingOutcome.manchesterCategory || 'Not specified'}
        - Presenting Complaint: ${existingOutcome.presentingComplaint || 'Not specified'}
        - Discriminator: ${existingOutcome.discriminator || 'Not specified'}
        `;
      }
      
      // Call GPT with specialized prompt for complex cases
      // Use the advanced model for complex case analysis
      const response = await this.callGPT({
        model: this.config.advancedModel, // Use advanced model for complex cases
        messages: [
          {
            role: 'system',
            content: `You are a clinical AI assistant specializing in complex emergency triage cases.
            Analyze this complex patient case with unusual symptom combinations or atypical presentations.
            Provide insights that enhance the existing triage assessment with special consideration for:
            1. Potential rare conditions or unusual presentations
            2. Interactions between symptoms that might change urgency
            3. Special clinical considerations based on the patient profile
            
            Return ONLY a JSON object with the following structure:
            {
              "confidence": number between 0-1,
              "additionalNotes": string with clinical insights,
              "additionalSteps": string array of recommended next steps,
              "redFlags": string array of potential red flags,
              "urgencyRecommendation": optional string with recommended urgency level
            }
            
            The urgency levels in the Manchester Triage System are:
            - "immediate" (Red): Immediate life-saving intervention
            - "urgent" (Orange): Very urgent, potentially life-threatening
            - "standard" (Yellow): Urgent but stable
            - "non-urgent" (Green): Standard, non-urgent care
            
            NOTE: Acute Psychosis is correctly classified as "standard" (Yellow) in the Manchester Triage System.
            `
          },
          { 
            role: 'user', 
            content: `Complex patient case:
            ${JSON.stringify(safeTriageData || {})}
            
            ${existingOutcomeContext}`
          }
        ],
        temperature: 0.3, // Slightly higher temperature for complex reasoning
        response_format: { type: 'json_object' }
      });
      
      if (response && response.choices && response.choices[0]?.message?.content) {
        try {
          const jsonResponse = JSON.parse(response.choices[0].message.content);
          
          // Validate and construct the AIInsight with proper type checking
          const validUrgencyLevels: UrgencyLevel[] = ['immediate', 'urgent', 'standard', 'non-urgent'];
          
          // Validate urgency recommendation if present
          let urgencyRecommendation: UrgencyLevel | undefined = undefined;
          if (jsonResponse.urgencyRecommendation && 
              validUrgencyLevels.includes(jsonResponse.urgencyRecommendation as UrgencyLevel)) {
            urgencyRecommendation = jsonResponse.urgencyRecommendation as UrgencyLevel;
          }
          
          return {
            confidence: typeof jsonResponse.confidence === 'number' ? jsonResponse.confidence : 0.7,
            additionalNotes: jsonResponse.additionalNotes || 'Complex case detected, requires clinical judgment.',
            additionalSteps: Array.isArray(jsonResponse.additionalSteps) ? jsonResponse.additionalSteps : 
              ['Consult with specialist', 'Consider additional diagnostics'],
            redFlags: Array.isArray(jsonResponse.redFlags) ? jsonResponse.redFlags : [],
            urgencyRecommendation // Only include if valid
          };
        } catch (error) {
          console.error('[AIService] Error parsing complex case analysis:', error);
          console.log('[AIService] Falling back to simulated insights due to parsing error');
          return this.generateSimulatedAIInsights(safeTriageData);
        }
      }
      
      // Fallback if API call failed to return valid data
      console.log('[AIService] API call did not return valid data, using fallback');
      return this.generateSimulatedAIInsights(safeTriageData);
      
    } catch (error) {
      console.error('[AIService] Error handling complex case:', error);
      console.log('[AIService] Falling back to simulated insights due to error');
      return this.generateSimulatedAIInsights(safeTriageData);
    }
  }
  
  /**
   * Enhance standard triage with AI insights
   * Uses the base model as this builds on existing triage outcomes
   * 
   * @param triageData The patient's triage data
   * @param existingOutcome Optional existing triage outcome
   * @returns AI-generated insights
   */
  private async enhanceTriage(triageData: TriageData, existingOutcome?: TriageOutcome): Promise<AIInsight> {
    console.log('[AIService] Enhancing standard triage case');
    
    // If no triage data provided, return simulated insights with empty data
    if (!triageData) {
      console.error('[AIService] No triage data provided for triage enhancement');
      const emptyData: TriageData = { 
        symptoms: [], 
        vitals: {}, 
        severity: '', 
        duration: '', 
        onset: '', 
        medicalHistory: [], 
        medications: [], 
        allergies: [], 
        additionalInfo: '', 
        description: '' 
      };
      return this.generateSimulatedAIInsights(emptyData);
    }
    
    // Create a safe copy of triage data with defaults to prevent null reference errors
    const safeTriageData: TriageData = {
      symptoms: triageData.symptoms || [],
      vitals: triageData.vitals || {},
      age: triageData.age || undefined, // Use undefined for optional number field
      gender: triageData.gender || '',
      severity: triageData.severity || '',
      duration: triageData.duration || '',
      onset: triageData.onset || '',
      medicalHistory: triageData.medicalHistory || [],
      medications: triageData.medications || [],
      allergies: triageData.allergies || [],
      additionalInfo: triageData.additionalInfo || '',
      description: triageData.description || ''
    };
    
    // If testing mode or no API key, fall back to simulation
    if (this.config.testingMode || !this.config.apiKey) {
      return this.generateSimulatedAIInsights(safeTriageData);
    }
    
    try {
      // Prepare context about the existing outcome if available
      let existingOutcomeContext = '';
      if (existingOutcome && typeof existingOutcome === 'object') {
        existingOutcomeContext = `
        Current triage assessment:
        - Urgency: ${existingOutcome.urgency || 'Not specified'}
        - Manchester Category: ${existingOutcome.manchesterCategory || 'Not specified'}
        - Presenting Complaint: ${existingOutcome.presentingComplaint || 'Not specified'}
        - Discriminator: ${existingOutcome.discriminator || 'Not specified'}
        `;
      }
      
      // Call GPT with standard triage enhancement prompt
      // Use the base model for standard triage enhancement
      const response = await this.callGPT({
        model: this.config.baseModel, // Use base model for standard cases
        messages: [
          {
            role: 'system',
            content: `You are a clinical AI assistant specializing in emergency triage.
            Analyze this patient case and enhance the existing triage assessment with additional insights.
            Focus on providing clinically relevant information that supports the triage decision.
            
            Return ONLY a JSON object with the following structure:
            {
              "confidence": number between 0-1,
              "additionalNotes": string with clinical insights,
              "additionalSteps": string array of recommended next steps,
              "redFlags": string array of potential red flags
            }
            
            Be concise, clinically accurate, and focus on information that would be helpful to healthcare providers.`
          },
          { 
            role: 'user', 
            content: `Patient case:
            ${JSON.stringify({
              symptoms: safeTriageData.symptoms,
              vitals: safeTriageData.vitals,
              age: safeTriageData.age,
              gender: safeTriageData.gender,
              medicalHistory: safeTriageData.medicalHistory,
              medications: safeTriageData.medications,
              allergies: safeTriageData.allergies,
              additionalInfo: safeTriageData.additionalInfo
            })}
            
            ${existingOutcomeContext}`
          }
        ],
        temperature: 0.2,
        response_format: { type: 'json_object' }
      });
      
      if (response && response.choices && response.choices[0]?.message?.content) {
        try {
          const jsonResponse = JSON.parse(response.choices[0].message.content);
          
          // Validate and construct the AIInsight
          return {
            confidence: typeof jsonResponse.confidence === 'number' ? jsonResponse.confidence : 0.8,
            additionalNotes: jsonResponse.additionalNotes || 'Standard triage assessment enhanced with AI insights.',
            additionalSteps: Array.isArray(jsonResponse.additionalSteps) ? jsonResponse.additionalSteps : [],
            redFlags: Array.isArray(jsonResponse.redFlags) ? jsonResponse.redFlags : []
          };
        } catch (error) {
          console.error('[AIService] Error parsing triage enhancement:', error);
          console.log('[AIService] Falling back to simulated insights due to parsing error');
          return this.generateSimulatedAIInsights(safeTriageData);
        }
      }
      
      // Fallback if API call failed to return valid data
      console.log('[AIService] API call did not return valid data, using fallback');
      return this.generateSimulatedAIInsights(safeTriageData);
      
    } catch (error) {
      console.error('[AIService] Error enhancing triage:', error);
      console.log('[AIService] Falling back to simulated insights due to error');
      return this.generateSimulatedAIInsights(safeTriageData);
    }
  }
  
  /**
   * Generate structured clinical summary for pre-visit preparation
   * Uses advanced AI model for comprehensive clinical analysis
   * 
   * @param inputData Comprehensive patient data for analysis
   * @returns Structured clinical summary with key problems, trends, and alerts
   */
  async generateStructuredSummary(inputData: any): Promise<any> {
    try {
      console.log('🤖 AIService: Generating structured clinical summary');
      
      // Prepare comprehensive clinical prompt
      const systemPrompt = `You are an expert clinical AI assistant specializing in pre-visit chart summarization for healthcare providers.

Your task is to analyze comprehensive patient data and generate a structured clinical summary that helps healthcare providers prepare for patient encounters.

Analyze the provided patient data and generate a JSON response with the following structure:
{
  "keyProblems": [
    {
      "problem": "Clear, concise problem statement",
      "severity": "low|moderate|high|critical",
      "category": "acute|chronic|preventive|follow-up",
      "confidence": 0.0-1.0,
      "supportingEvidence": ["Evidence point 1", "Evidence point 2"]
    }
  ],
  "vitalTrends": [
    {
      "parameter": "Parameter name",
      "parameterCode": "Standard code",
      "trend": "improving|stable|declining|concerning",
      "values": [{"date": "ISO date", "value": number, "unit": "unit"}],
      "significance": "normal|concerning|critical",
      "recommendation": "Clinical recommendation"
    }
  ],
  "medicationAlerts": [
    {
      "type": "interaction|contraindication|dosage|allergy",
      "severity": "low|moderate|high",
      "description": "Alert description",
      "recommendation": "Clinical recommendation",
      "medications": ["Med 1", "Med 2"],
      "clinicalSignificance": "Clinical impact explanation"
    }
  ],
  "triageContext": {
    "currentTriageLevel": number,
    "triageReason": "Primary concern",
    "urgencyFactors": ["Factor 1", "Factor 2"],
    "recommendedActions": ["Action 1", "Action 2"],
    "timeToProvider": "Recommended timeframe",
    "specialConsiderations": ["Consideration 1"]
  },
  "confidence": 0.0-1.0,
  "modelVersion": "Model identifier"
}

Focus on:
1. Clinically relevant problems requiring provider attention
2. Significant trends in vital signs or lab values
3. Medication safety alerts and interactions
4. Current triage context and urgency factors
5. Actionable recommendations for the healthcare provider

Ensure all recommendations are evidence-based and clinically appropriate. Maintain patient safety as the highest priority.`;

      const userPrompt = `Please analyze the following patient data and generate a structured clinical summary:

${JSON.stringify(inputData, null, 2)}

Provide a comprehensive pre-visit summary that will help the healthcare provider prepare for this patient encounter.`;

      const request: GPTRequest = {
        model: this.config.advancedModel, // Use advanced model for clinical analysis
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        temperature: 0.2, // Lower temperature for more consistent clinical analysis
        max_tokens: 2000,
        response_format: { type: 'json_object' }
      };

      const response = await this.callGPT(request);
      
      if (response?.choices?.[0]?.message?.content) {
        try {
          const summary = JSON.parse(response.choices[0].message.content);
          
          // Validate and enhance the response
          return {
            keyProblems: Array.isArray(summary.keyProblems) ? summary.keyProblems : [],
            vitalTrends: Array.isArray(summary.vitalTrends) ? summary.vitalTrends : [],
            medicationAlerts: Array.isArray(summary.medicationAlerts) ? summary.medicationAlerts : [],
            triageContext: summary.triageContext || {},
            confidence: typeof summary.confidence === 'number' ? summary.confidence : 0.8,
            modelVersion: this.config.advancedModel
          };
        } catch (parseError) {
          console.error('Error parsing structured summary response:', parseError);
          return this.generateFallbackSummary(inputData);
        }
      }
      
      // Fallback if API call failed
      console.log('API call failed, using fallback summary generation');
      return this.generateFallbackSummary(inputData);
      
    } catch (error) {
      console.error('Error generating structured summary:', error);
      return this.generateFallbackSummary(inputData);
    }
  }

  /**
   * Generate fallback clinical summary when AI service is unavailable
   * 
   * @param inputData Patient data for analysis
   * @returns Basic structured summary
   */
  private generateFallbackSummary(inputData: any): any {
    console.log('🔄 AIService: Generating fallback clinical summary');
    
    const fallbackSummary = {
      keyProblems: [],
      vitalTrends: [],
      medicationAlerts: [],
      triageContext: {},
      confidence: 0.6,
      modelVersion: 'fallback-v1'
    };

    // Extract basic problems from triage context if available
    if (inputData.triageContext?.reason) {
      fallbackSummary.keyProblems.push({
        problem: inputData.triageContext.reason,
        severity: 'moderate',
        category: 'acute',
        confidence: 0.7,
        supportingEvidence: ['Patient-reported chief complaint']
      });
    }

    // Extract basic triage context
    if (inputData.triageContext) {
      fallbackSummary.triageContext = {
        currentTriageLevel: inputData.triageContext.level || 3,
        triageReason: inputData.triageContext.reason || 'General assessment',
        urgencyFactors: inputData.triageContext.symptoms || [],
        recommendedActions: ['Complete clinical assessment', 'Review vital signs'],
        timeToProvider: 'Standard scheduling',
        specialConsiderations: ['AI service unavailable - manual review recommended']
      };
    }

    // Basic medication alert if allergies present
    if (inputData.patientData?.allergies?.length > 0) {
      fallbackSummary.medicationAlerts.push({
        type: 'allergy',
        severity: 'high',
        description: `Patient has known allergies: ${inputData.patientData.allergies.join(', ')}`,
        recommendation: 'Verify all medications against allergy list',
        medications: [],
        clinicalSignificance: 'Prevent allergic reactions'
      });
    }

    return fallbackSummary;
  }

  /**
   * Simulated AI insights generation
   * This replicates the current functionality from useTriageAssessment.ts
   * 
   * @param triageData The patient's triage data
   * @returns Simulated AI insights with urgency recommendation
   */
  private generateSimulatedAIInsights(triageData: TriageData): AIInsight {
    // Simulate AI analysis
    const hasMultipleSymptoms = (triageData.symptoms || []).length > 3;
    const hasVitals = Object.keys(triageData.vitals || {}).length > 0;
    const hasMedicalHistory = (triageData.medicalHistory || []).length > 0;
    
    let confidence = 0.7;
    let additionalNotes = '';
    let additionalSteps: string[] = [];
    let redFlags: string[] = [];
    let urgencyRecommendation: UrgencyLevel = 'standard'; // Default to standard urgency
    
    if (hasVitals) {
      confidence += 0.1;
      additionalNotes += 'Vital signs provided improve assessment accuracy. ';
    }
    
    if (hasMedicalHistory) {
      confidence += 0.1;
      additionalNotes += 'Medical history considered in assessment. ';
    }
    
    if (hasMultipleSymptoms) {
      additionalNotes += 'Multiple symptoms pattern suggests comprehensive evaluation needed. ';
      additionalSteps.push('Provide complete symptom timeline to healthcare provider');
    }
    
    // Check for specific symptom patterns
    const symptoms = (triageData.symptoms || []).map(s => s.toLowerCase());
    
    // Check for high urgency patterns
    if (symptoms.includes('chest pain') && symptoms.includes('shortness of breath')) {
      additionalNotes += 'Cardiorespiratory symptoms require prompt evaluation. ';
      additionalSteps.push('Avoid physical exertion until medical evaluation');
      redFlags.push('Combined chest pain and shortness of breath');
      urgencyRecommendation = 'urgent'; // Increase urgency for cardiorespiratory symptoms
    }
    
    // Additional pattern: High fever with rash
    if (symptoms.includes('fever') && symptoms.includes('rash')) {
      additionalNotes += 'Fever with rash may indicate infectious process requiring evaluation. ';
      redFlags.push('Fever with rash - possible infectious disease');
      urgencyRecommendation = 'urgent'; // Increase urgency for potential infectious disease
    }
    
    // Check for immediate urgency patterns
    if (symptoms.includes('unconscious') || symptoms.includes('not breathing')) {
      additionalNotes += 'Critical symptoms detected requiring immediate attention. ';
      redFlags.push('Potential life-threatening condition');
      urgencyRecommendation = 'immediate'; // Set to highest urgency for critical symptoms
    }
    
    return {
      confidence: Math.min(confidence, 0.95),
      additionalNotes,
      additionalSteps,
      redFlags,
      urgencyRecommendation, // Include urgency recommendation
    };
  }
}

// Create and export a singleton instance with tiered model configuration
export const aiService = new AIService({
  testingMode: false, // Set to false to use real API with your key from .env
  baseModel: 'gpt-3.5-turbo', // Cost-effective model for basic tasks
  advancedModel: 'gpt-4-turbo' // More capable model for complex cases
  // API key will be loaded from environment variables
});

// Export types for use in other modules
export type { GPTMessage, GPTRequest, GPTResponse, AIServiceConfig };
